import React, {ChangeEvent, useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';

import {WorkflowItem} from '../types';
import {LoadingIndicator} from '../components/LoadingIndicator';
import {ButtonGroup} from '../components/ButtonGroup';
import {User} from '../_services/User';
import {Flow} from '../_services/Flow';
import {WorkflowListItem} from '../components/WorkflowListItem';

type WorkflowListProps = {};

type Tag = {
    tag: string;
    title: string;
    showButton: boolean;
}


export const WorkflowList: React.FunctionComponent<WorkflowListProps> = ({}): JSX.Element => {
    const {filterType, filter} = useParams();

    const [workflows, setWorkflows] = useState<any>([]);
    const [workflowsMapped, setWorkflowsMapped] = useState<any>({});
    const [groups, setGroups] = useState<string[]>([]);
    const [groupOrder, setGroupOrder] = useState<string[]>([]);
    const [tagButtons, setTagButtons] = useState<Tag[]>([]);
    const [tagCategoryMap, setTagCategoryMap] = useState<any>({});
    const [selectedTags, setSelectedTags] = useState<Tag[]>(User.getInstance().getSelectedTags());
    const [workflowErrorMessage, setWorkflowErrorMessage]: any = useState('');
    const [workflowIsLoading, setWorkflowIsLoading]: any = useState(false);
    const [workflowListFilterText, setWorkflowListFilterText] = useState<string>('');
    const [userFavorites, setUserFavorites] = useState<any>({});

    useEffect(() => {
        User.getInstance().setSelectedTags(selectedTags);
    }, [selectedTags]);

    useEffect(() => {
        setWorkflowIsLoading(true);
        setWorkflows([]);

        Flow.getInstance().getWorkflows(filterType, filter)
            .then(response => {
                if (response.ok && response.json.workflowsGrouped) {
                    setWorkflows(response.json.workflowsGrouped);
                    setGroups(response.json.groups);
                    setGroupOrder(response.json.groupOrder);
                    setUserFavorites(response.json?.userFavorites || {});

                    setWorkflowsMapped(
                        response.json.workflows.reduce((acc: any, item: WorkflowItem) => {
                            return {
                                ...acc,
                                [item.id]: item
                            }
                        }, {})
                    );

                    if (response.json.tags) {
                        setTagButtons(
                            Object.keys(response.json.tags)
                                // filter on tags where we want to show a button
                                .filter((key: string) => response.json.tags[key].showButton)
                                // filter on tags where at least one workflow references the tag
                                .filter((key: string) => response.json.workflows.some((workflow: any) => workflow.tags.includes(key)))
                                .map((key: string) => ({
                                    ...response.json.tags[key],
                                    label: response.json.tags[key].title,
                                    value: response.json.tags[key].tag
                                }))
                        );
                        setTagCategoryMap(response.json.tagCategoryMap);
                    }
                } else {
                    setWorkflowErrorMessage(response.json.message || 'Error fetching Workflows');
                }
                setWorkflowIsLoading(false);
            })
            .catch((error: any) => {
                setWorkflowErrorMessage(error || 'Exception fetching Workflows');
                setWorkflowIsLoading(false);
            });
    }, [false]);

    const filterWorkflows = (workflow: WorkflowItem) => (
        workflow.name.match(new RegExp(workflowListFilterText, 'i')) ||
        workflow.description.match(new RegExp(workflowListFilterText, 'i'))
    );

    const handlePinWorkflow = (pinned: boolean, workflowId: number) => {
        setUserFavorites({
            ...userFavorites,
            [workflowId]: pinned
        });

        // ignore errors
        if (pinned) {
            Flow.getInstance().saveFavorite(workflowId);
        } else {
            Flow.getInstance().deleteFavorite(workflowId);
        }
    };

    const searchFilterContainerClasses = [
        'input-group',
        'mb-3',
        'search-filter-input-container',
        workflowListFilterText && 'filtered'
    ]
        .filter(Boolean)
        .join(' ');

    const userFavoritesArray = Object.keys(userFavorites).filter((workflowId: any) => userFavorites[workflowId] === true);

    return (
        <div>
            {workflowIsLoading && <LoadingIndicator/>}
            {workflowErrorMessage && <p className="alert alert-danger">{workflowErrorMessage}</p>}
            {workflows &&
            <div className="row">
                <div className="col-8 offset-2">
                    <div className={searchFilterContainerClasses}>
                        <input type="text"
                               className="form-control form-control-lg"
                               value={workflowListFilterText}
                               onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                   setWorkflowListFilterText(e.target.value);
                               }}
                               placeholder="Filter Workflows"
                        />
                        {workflowListFilterText &&
                        <div className="input-group-append">
                                <span className="input-group-text"
                                      id="basic-addon2"
                                      style={{
                                          borderLeft: 'none',
                                          background: 'transparent'
                                      }}
                                >
                                    <i className="fas fa-times"
                                       onClick={() => {
                                           setWorkflowListFilterText('')
                                       }}
                                    />
                                </span>
                        </div>
                        }
                    </div>
                </div>
            </div>
            }
            {workflows && tagButtons && tagButtons.length > 0 &&
            <div className="d-flex justify-content-center mb-2">
                <ButtonGroup
                    choices={tagButtons as any}
                    value={selectedTags as any}
                    onChange={(selected) => {
                        setSelectedTags(selected);
                    }}/>
            </div>
            }
            {userFavoritesArray.length > 0 &&
            <div className="mb-5">
                <h3 className="category-heading">Pinned Workflows</h3>
                <div className="card-group"
                     id="workflow-list"
                >
                    {userFavoritesArray.map((workflowId: number) => {
                        const workflowItem = workflowsMapped.hasOwnProperty(workflowId) && workflowsMapped[workflowId];
                        return (
                            workflowItem && <WorkflowListItem
                                key={workflowItem.id}
                                workflowItem={workflowItem}
                                isPinned={userFavorites.hasOwnProperty(workflowItem.id) && userFavorites[workflowItem.id]}
                                pin={(shouldPin: boolean) => {
                                    handlePinWorkflow(shouldPin, workflowItem.id);
                                }}
                            />
                        );
                    })}
                </div>
            </div>
            }
            {workflows && groupOrder
                .filter((groupId: string) => {
                    return (
                        !tagCategoryMap ||
                        selectedTags.length === 0 ||
                        selectedTags.some((tag: Tag) => !tagCategoryMap.hasOwnProperty(tag.tag) || tagCategoryMap[tag.tag].find((group: string) => group === groupId) !== undefined)
                    )
                })
                .map((groupId: any, index: number) => (
                        workflows.hasOwnProperty(groupId) && workflows[groupId].filter(filterWorkflows).length > 0 &&
                        <div key={index} className="mb-5">
                            <h3 className="category-heading">{groups[groupId]}</h3>
                            <div className="card-group"
                                 id="workflow-list"
                            >
                                {
                                    workflows[groupId].filter(filterWorkflows)
                                        .sort((a: WorkflowItem, b: WorkflowItem) => {
                                            if (a.groupSortOrder < b.groupSortOrder) {
                                                return -1;
                                            } else if (a.groupSortOrder > b.groupSortOrder) {
                                                return 1;
                                            } else {
                                                return 0;
                                            }
                                        })
                                        .map((workflowItem: WorkflowItem) => (
                                        <WorkflowListItem
                                            key={workflowItem.id}
                                            workflowItem={workflowItem}
                                            isPinned={userFavorites.hasOwnProperty(workflowItem.id) && userFavorites[workflowItem.id]}
                                            pin={(shouldPin: boolean) => {
                                                handlePinWorkflow(shouldPin, workflowItem.id);
                                            }}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                    )
                )}
        </div>
    )
};